import type {
  AcceptableAlternatives,
  QuotationSupplierProduct,
  RestaurantInquiryItem,
} from 'apis/restaurant-inquiries';
import {
  isEmptySpec,
  packagingCompatible,
  packagingSatisfiesAlternative,
  packagingSpecOf,
} from 'pages/inquiry-processor/utils/packaging-spec';

// Light singularizer so "Tomato" and "Tomatoes" compare as the same token —
// covers the regular English plurals catalog nouns actually use: -ies → -y
// (berries), -oes/-xes/-ches/-shes/-sses/-zes → drop "es" (tomatoes, boxes,
// peaches), plain -s → drop it (carrots); -ss/-us/-is endings are not plurals
// (glass, hummus). Mirrors the BE's singularizeToken — keep the two in parity.
const singularize = (t: string): string => {
  if (t.length < 4 || !t.endsWith('s') || /(ss|us|is)$/.test(t)) return t;
  if (t.endsWith('ies')) return `${t.slice(0, -3)}y`;
  if (/(oes|xes|ches|shes|sses|zes)$/.test(t)) return t.slice(0, -2);
  return t.slice(0, -1);
};

// Tokenize for name matching: lowercase, split on non-alphanumerics, drop
// 1-char noise, singularize.
const tokenize = (s: string): string[] =>
  s
    .toLowerCase()
    .split(/[^a-z0-9]+/)
    .filter((t) => t.length >= 2)
    .map(singularize);

// The suggestion contract, shared with the BE suggested-products endpoint
// (user-mngt-srv quotation-suggestion-score.helper): only exact-tier name
// matches (100 identical, 98 reordered) clear the 95 threshold — a mere
// token-coverage score let "Tomato paste" pass as a match for "Tomato", which
// is neither exact nor a real semantic match. At most 6 suggestions (two rows
// of three cards).
export const SUGGESTION_MATCH_THRESHOLD = 95;
export const SUGGESTION_LIMIT = 6;

export interface RankedSuggestion {
  product: QuotationSupplierProduct;
  // 0–100 — the same meaning as the BE's matchScore, so both sources render
  // identically: 100 = identical name, 98 = same words reordered, otherwise a
  // Dice-similarity score that never reaches the threshold.
  matchScore: number;
}

// Mirrors the BE's scoreProductName exactly — keep the two in parity.
export function scoreProductName(
  requestName: string | null | undefined,
  productName: string | null | undefined
): number {
  const requestTokens = tokenize(requestName || '');
  const productTokens = tokenize(productName || '');
  if (!requestTokens.length || !productTokens.length) return 0;
  if (requestTokens.join(' ') === productTokens.join(' ')) return 100;
  const requestSet = new Set(requestTokens);
  const productSet = new Set(productTokens);
  const overlap = [...requestSet].filter((t) => productSet.has(t)).length;
  if (overlap === requestSet.size && overlap === productSet.size) return 98;
  const dice = (2 * overlap) / (requestSet.size + productSet.size);
  return Math.round(dice * 90);
}

type FlexibleKey = 'brand' | 'origin' | 'state' | 'spec';

// The item fields the suggestion gate needs — a subset of RestaurantInquiryItem
// so callers (and tests) don't have to construct a full item. `id` feeds the
// BE suggested-products endpoint (?itemId=), which loads flexibility/master
// product fields itself.
export type SuggestionRequestItem = Pick<
  RestaurantInquiryItem,
  'id' | 'brandFlexible' | 'originFlexible' | 'acceptableAlternatives' | 'masterProduct'
> &
  Partial<Pick<RestaurantInquiryItem, 'specFlexible' | 'stateFlexible' | 'packagingFlexible'>>;

const norm = (v?: string | null) => (v || '').trim().toLowerCase();

// Mirrors the BE's attributesCompatible exactly: baseUnit is always strict when
// both sides know it; brand/origin/state/spec and pack format only gate while a
// value on both sides conflicts — a flexible attribute widens to its
// acceptableAlternatives list (or any value, with no list). Unknown values on
// either side never disqualify a candidate — a human confirms every suggestion.
export function attributesCompatible(item: SuggestionRequestItem, product: QuotationSupplierProduct): boolean {
  const mp = item.masterProduct;
  const masterUom = norm(mp?.baseUnit);
  const productUom = norm(product.defaultUnit);
  if (masterUom && productUom && masterUom !== productUom) return false;

  const flexibility: Record<FlexibleKey, boolean> = {
    brand: item.brandFlexible,
    origin: item.originFlexible,
    state: item.stateFlexible ?? true,
    spec: item.specFlexible ?? true,
  };
  const masterValues: Record<FlexibleKey, string> = {
    brand: norm(mp?.brand),
    origin: norm(mp?.origin),
    state: norm(mp?.state),
    spec: norm(mp?.spec),
  };
  const productValues: Record<FlexibleKey, string> = {
    brand: norm(product.brandEn),
    origin: norm(product.origin),
    state: norm(product.state),
    spec: norm(product.specs),
  };
  const alternatives: AcceptableAlternatives = item.acceptableAlternatives ?? {};

  for (const key of ['brand', 'origin', 'state', 'spec'] as FlexibleKey[]) {
    const masterValue = masterValues[key];
    const productValue = productValues[key];
    if (!masterValue || !productValue || masterValue === productValue) continue;
    if (!flexibility[key]) return false;
    const list = (alternatives[key] ?? []).map((v) => v.trim().toLowerCase()).filter(Boolean);
    if (list.length && !list.includes(productValue)) return false;
  }

  // Pack format, symmetric with the string attributes above. The request's own
  // format always passes; a fixed flag makes a conflict disqualifying; a
  // non-empty list narrows a flexible flag to the listed formats. Fields
  // unknown on either side are skipped inside packagingCompatible, so sparse
  // supplier pack data can never empty the suggestion list against the
  // request's own default format.
  const requestPackaging = packagingSpecOf(mp);
  const candidatePackaging = packagingSpecOf(product.masterProduct);
  if (!packagingCompatible(requestPackaging, candidatePackaging)) {
    if (!(item.packagingFlexible ?? true)) return false;
    const packagingAlternatives = (alternatives.packaging ?? [])
      .map(packagingSpecOf)
      .filter((spec) => !isEmptySpec(spec));
    // packagingSatisfiesAlternative, NOT packagingCompatible — narrowing is
    // one-directional. See its docblock in utils/packaging-spec; the symmetric
    // predicate would let a candidate's unrelated unknown fields satisfy every
    // listed alternative. The BE gate uses the same pairing.
    if (
      packagingAlternatives.length &&
      !packagingAlternatives.some((spec) => packagingSatisfiesAlternative(spec, candidatePackaging))
    ) {
      return false;
    }
  }

  return true;
}

// Deterministic client-side ranking for the suggestion cards — the fallback
// when the BE suggested-products endpoint isn't deployed yet. Exact-tier name
// score gates entry (>=95); brand/origin/state/spec gate too, per the item's
// flexibility. An exact unit/brand match and a live supplier price only
// break ties among exact-tier matches.
export function rankCatalogSuggestions(
  products: QuotationSupplierProduct[],
  item: SuggestionRequestItem | undefined,
  max = SUGGESTION_LIMIT
): RankedSuggestion[] {
  const requestName = item?.masterProduct?.name;
  if (!tokenize(requestName || '').length) return [];

  const requestUnit = norm(item?.masterProduct?.baseUnit);
  const requestBrand = norm(item?.masterProduct?.brand);

  const scored = products
    .map((product) => ({ product, matchScore: scoreProductName(requestName, product.nameEn) }))
    .filter(({ matchScore }) => matchScore >= SUGGESTION_MATCH_THRESHOLD)
    .filter(({ product }) => attributesCompatible(item as SuggestionRequestItem, product));

  const hasActiveDeal = (p: QuotationSupplierProduct) => (p.supplierPrices?.[0]?.isActive ? 1 : 0);
  const unitMatches = (p: QuotationSupplierProduct) => (requestUnit && norm(p.defaultUnit) === requestUnit ? 1 : 0);
  const brandMatches = (p: QuotationSupplierProduct) => (requestBrand && norm(p.brandEn) === requestBrand ? 1 : 0);

  return scored
    .sort(
      (a, b) =>
        b.matchScore - a.matchScore ||
        hasActiveDeal(b.product) - hasActiveDeal(a.product) ||
        unitMatches(b.product) - unitMatches(a.product) ||
        brandMatches(b.product) - brandMatches(a.product) ||
        (a.product.nameEn || '').localeCompare(b.product.nameEn || '')
    )
    .slice(0, max);
}

// The most distinctive request token — the server-side search term when page 1
// can't hold the whole catalog. iLike '%full name%' almost never matches, so a
// single longest-token probe recovers the likeliest candidates. Tokens are
// singularized, and a suffix-stripped singular is a prefix of every surface
// form except the -y/-ies pair ("berry" ⊄ "berries") — trim the trailing "y"
// so the substring probe catches both.
export function searchProbe(name?: string | null): string | undefined {
  const tokens = tokenize(name || '').filter((t) => t.length >= 3);
  if (!tokens.length) return undefined;
  const probe = [...tokens].sort((a, b) => b.length - a.length)[0];
  return probe.length >= 4 && probe.endsWith('y') ? probe.slice(0, -1) : probe;
}
